import { Button, Stack, Typography } from "@mui/material";
import { colours } from "../../../StyleConstants";
import StepperDUC from "../../duc/StepperDUC";
import React, { useState, useEffect } from "react";
import Selector from "./Selector";
import { useSelector } from "react-redux";
import { selectSelectedDatabase, selectSelectedStudy } from "../../common/databaseSlice";
import { useGetDatabaseVersionQuery } from "../../../api/databaseApiSlice";
import { useNavigate } from "react-router-dom";
import Continue from "./Continue";
import ToolSelectModal from "../../new-data-wrangler/FileDownload/ToolSelectModal";
import { useGetPreviousReleasesQuery } from "../../../api/previousReleaseApiSlice";
import { File, FileDataset, Release } from "../Types";

export enum DownloadProcessSteps {
    ContinueModel = 0,
    ReleaseSelection = 1,
    DatasetSelection = 2,
    FileSelection = 3
}
export const stepLabels = [
    "RELEASE SELECTION",
    "DATASET SELECTION",
    "FILE SELECTION",
];


const PreviousRelease = () => {
    const selectedDatabase = useSelector(selectSelectedDatabase);
    const selectedStudy = useSelector(selectSelectedStudy);
    const { data: version } = useGetDatabaseVersionQuery({ selectedDatabase });
    const { data: apiData, isLoading, isFetching } = useGetPreviousReleasesQuery({ selectedDatabase });
    const [curStep, setCurStep] = useState(DownloadProcessSteps.ContinueModel);
    const [selectedRelease, setSelectedRelease] = useState<number>(-1);
    const [selectedDataset, setSelectedDataset] = useState<number>(-1);
    const [selectedFile, setSelectedFile] = useState<number>(-1);
    const [open, setOpen] = useState(false);
    const navigate = useNavigate();
            
    useEffect(() => {
        setSelectedRelease(-1);
        setSelectedDataset(-1);
        setSelectedFile(-1);
        setCurStep(DownloadProcessSteps.ContinueModel);
    }, [selectedDatabase]);

    const handleClickBack = () => {
        /* eslint-disable */
        // Disabling since switch statements should be indented
        switch (curStep) {
            case DownloadProcessSteps.ReleaseSelection:
                setCurStep(DownloadProcessSteps.ContinueModel);
                break;
            case DownloadProcessSteps.DatasetSelection:
                setSelectedRelease(-1);
                setCurStep(DownloadProcessSteps.ReleaseSelection);
                break;
            case DownloadProcessSteps.FileSelection:
                setSelectedFile(-1);
                setSelectedDataset(-1);
                setCurStep(DownloadProcessSteps.DatasetSelection);
                break;
        }
        /* eslint-enable */
    }

    const handleReleaseSelection = (index: number) => {
        setSelectedRelease(index);
        setCurStep(DownloadProcessSteps.DatasetSelection);
    }

    const handleDatasetSelection = (index: number) => {
        setSelectedDataset(index);
        setCurStep(DownloadProcessSteps.FileSelection);
    }

    const handleFileSelection = (index: number) => {
        setSelectedFile(index);
    }

    const onSubmitGlobus = () => {
        localStorage.setItem("globus-transfer-type", "previous");
        localStorage.setItem("globus-file-list", JSON.stringify({
            version: apiData[selectedRelease]?.name,
            dataset: apiData[selectedRelease]?.file_datasets[selectedDataset].name,
            pipeline: apiData[selectedRelease]?.file_datasets[selectedDataset].files[selectedFile].pipeline,
            sourceSummary: apiData[selectedRelease]?.file_datasets[selectedDataset].files[selectedFile].sourceSummary
        }));
        navigate("/globus-collections");
    }

    if (curStep === DownloadProcessSteps.ContinueModel) {
        return <Continue continue={() => setCurStep(DownloadProcessSteps.ReleaseSelection) }/>
    }

    if (isFetching || isLoading) {
        return <div>loading...</div>
    }

    return (
        <Stack flexDirection="column" gap={4} height="100%" marginX="50px">
            <Stack gap={3} sx={{ padding: 3 }}>
                <Typography
                    variant="h3"
                    fontWeight="600"
                    fontSize="40px"
                    textAlign="center"
                    color={colours.midnightBlue.blue}>
                    Previous Dataset Download
                </Typography>
                <StepperDUC
                    activeStep={curStep}
                    stepLabels={stepLabels}
                />
            </Stack>

            <Stack direction="row" justifyContent="space-between" marginTop={3}>
                <Button
                    variant="tertiary"
                    onClick={() => handleClickBack()}
                >
                    Back
                </Button>
            </Stack>

            <Stack direction="row" gap={3} sx={{ marginTop: 3 }}>
                <Stack flex={1} gap={2}>
                    <Typography variant="h6" fontWeight="600" color={colours.orange.orange}>
                        Selected study
                    </Typography>
                    <Selector title={selectedStudy} text={selectedStudy} selected={true}/>
                </Stack>
                <Stack flex={2} gap={2}>
                    <Typography variant="h6" fontWeight="600" color={selectedRelease >= 0 ? colours.orange.orange : colours.midnightBlue.blue}>
                        {selectedRelease >= 0 ? "Selected" : "Select a"} release
                    </Typography>
                    {selectedRelease >= 0 ? (
                        <Selector
                            title={apiData[selectedRelease]?.name}
                            text={"Previous Release"}
                            selected={true}
                        />
                    ) : (
                        <>
                            <Selector
                                title={version}
                                text={"Current Release"}
                                selected={false}
                                handleClick={() => navigate('/data-wrangler')}
                            />
                            {apiData.map((release: Release, index: number) => {
                                return (
                                    <Selector
                                        key={release.name}
                                        title={release.name}
                                        text={"Previous Release"}
                                        selected={index === selectedRelease}
                                        handleClick={() => handleReleaseSelection(index)}
                                    />
                                )
                            })}
                        </>
                    )}
                </Stack>
                {selectedRelease >= 0 && (
                    <Stack flex={2} gap={2}>
                        <Typography variant="h6" fontWeight="600" color={selectedDataset >= 0 ? colours.orange.orange : colours.midnightBlue.blue}>
                            {selectedDataset >= 0 ? "Selected" : "Select a"} dataset
                        </Typography>
                        {selectedDataset >= 0 ? (
                            <Selector
                                title={apiData[selectedRelease]?.file_datasets[selectedDataset]?.name}
                                text={"Dataset"}
                                selected={true}
                            />
                        ) : (
                            <>
                                {(apiData[selectedRelease]?.file_datasets || []).map((dataset: FileDataset, index: number) => {
                                    return (
                                        <Selector
                                            key={dataset.name}
                                            title={dataset.name}
                                            text={"Dataset"}
                                            selected={index === selectedDataset}
                                            handleClick={() => handleDatasetSelection(index)}
                                        />
                                    )
                                })}
                            </>
                        )}
                    </Stack>
                )}
            </Stack>
            {selectedDataset >= 0 && (
                <Stack
                    gap={2}
                    sx={{
                        width: '100%',
                        height: 'calc(100% - 570px)'
                    }}
                    overflow="auto"
                >
                    {apiData[selectedRelease]?.file_datasets[selectedDataset].files.map((file: File, index: number) => {
                        return (
                            <Selector
                                key={file.pipeline ? file.pipeline : file.category}
                                title={file.pipeline ? file.pipeline : file.category}
                                text={file.pipeline ? file.category : ''}
                                skinny={true}
                                selected={selectedFile === index}
                                handleClick={() => handleFileSelection(index)}
                                size={file.size}
                                fileCount={`${file.count} files`}
                            />
                        )
                    })}
                </Stack>
            )}
            {selectedDataset >= 0 && (
                <Button
                    variant="primary"
                    disabled={selectedFile < 0}
                    onClick={() => setOpen(true)}
                    sx={{ marginBottom: "24px" }}
                >
                    Trigger Transfer
                </Button>
            )}
            <ToolSelectModal open={open} setOpen={setOpen} onsubmit={onSubmitGlobus}/>
        </Stack>
    )
}

export default PreviousRelease;